import { lazy, Suspense, useEffect, useRef } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LeftNav } from '@/components/layout/LeftNav';
import { Header } from '@/components/layout/Header';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { SkeletonCard } from '@/components/shared/SkeletonCard';
import { ConnectionWatcher } from '@/components/shared/ConnectionWatcher';
import { SiteProvider, SiteLayout, useCurrentSite, resolveLastSite } from '@/contexts/SiteContext';
import { UnsavedSwitchModal } from '@/components/layout/UnsavedSwitchModal';
import { HelpDrawerProvider } from '@/components/help/HelpDrawerContext';
import { PublishQueueProvider } from '@/contexts/PublishQueueContext';
import { ContextualHelpDrawer } from '@/components/help/ContextualHelpDrawer';
import { useAuth } from '@/hooks/useAuth';
import { useSetupStatus } from '@/hooks/useSetupStatus';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { REDIRECT_STORAGE_KEY } from '@/api/client';

const LoginPage = lazy(() =>
  import('@/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const SetupPage = lazy(() =>
  import('@/pages/SetupPage').then((m) => ({ default: m.SetupPage })),
);
const BrainstormPage = lazy(() =>
  import('@/pages/BrainstormPage').then((m) => ({ default: m.BrainstormPage })),
);
const PipelinePage = lazy(() =>
  import('@/pages/PipelinePage').then((m) => ({ default: m.PipelinePage })),
);
const DraftsPage = lazy(() =>
  import('@/pages/DraftsPage').then((m) => ({ default: m.DraftsPage })),
);
const DraftDetailPage = lazy(() =>
  import('@/pages/DraftsPage').then((m) => ({ default: m.DraftDetailPage })),
);
const ArticlesPage = lazy(() =>
  import('@/pages/ArticlesPage').then((m) => ({ default: m.ArticlesPage })),
);
const ArticleDetailPage = lazy(() =>
  import('@/pages/ArticlesPage').then((m) => ({ default: m.ArticleDetailPage })),
);
const QueuePage = lazy(() =>
  import('@/pages/QueuePage').then((m) => ({ default: m.QueuePage })),
);
const ContentGraphPage = lazy(() =>
  import('@/pages/ContentGraphPage').then((m) => ({ default: m.ContentGraphPage })),
);
const AnalyticsPage = lazy(() =>
  import('@/pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })),
);
const AlertsPage = lazy(() =>
  import('@/pages/AlertsPage').then((m) => ({ default: m.AlertsPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const SitesPage = lazy(() =>
  import('@/pages/SitesPage').then((m) => ({ default: m.SitesPage })),
);
const SiteDetailPage = lazy(() =>
  import('@/pages/SiteDetailPage').then((m) => ({ default: m.SiteDetailPage })),
);
const PromptsPage = lazy(() =>
  import('@/pages/PromptsPage').then((m) => ({ default: m.PromptsPage })),
);
const NewPageForm = lazy(() =>
  import('@/pages/NewPageForm').then((m) => ({ default: m.NewPageForm })),
);
const PageDetail = lazy(() =>
  import('@/pages/PageDetail').then((m) => ({ default: m.PageDetail })),
);
const HelpPage = lazy(() =>
  import('@/pages/HelpPage').then((m) => ({ default: m.HelpPage })),
);

function RoutePendingSkeleton() {
  return (
    <div className="space-y-6">
      <SkeletonCard />
      <SkeletonCard />
    </div>
  );
}

// ProtectedShell renders the dashboard chrome and outlet. Mounted inside
// <SiteProvider> and <HelpDrawerProvider> so chrome components can read the
// active-site context and surface the contextual help drawer.
function ProtectedShell() {
  const mainRef = useRef<HTMLElement | null>(null);
  useScrollRestoration(mainRef);
  useDocumentTitle();
  const { user } = useAuth();
  if (!user) return null;
  return (
    <PublishQueueProvider>
      <div className="flex h-full">
        <LeftNav />
        <div className="flex-1 flex flex-col min-w-0">
          <Header user={user} />
          <main ref={mainRef} className="flex-1 overflow-y-auto px-6 py-6">
            <Suspense fallback={<RoutePendingSkeleton />}>
              <Outlet />
            </Suspense>
          </main>
        </div>
        <ConnectionWatcher />
        <UnsavedSwitchModal />
        <ContextualHelpDrawer />
      </div>
    </PublishQueueProvider>
  );
}

function ProtectedLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // After a successful auth check, if the session-expiry handler stashed a
  // redirect target in sessionStorage, bounce the user back to that path.
  // The backend OAuth callback always lands at `/`, so this effect — not the
  // LoginPage one — is what actually fires in the common flow.
  useEffect(() => {
    if (loading || !user) return;
    let stored: string | null = null;
    try {
      stored = window.sessionStorage.getItem(REDIRECT_STORAGE_KEY);
    } catch {
      stored = null;
    }
    if (!stored || stored === location.pathname + location.search) {
      if (stored) {
        try {
          window.sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
        } catch {
          // ignored
        }
      }
      return;
    }
    try {
      window.sessionStorage.removeItem(REDIRECT_STORAGE_KEY);
    } catch {
      // ignored
    }
    navigate(stored, { replace: true });
  }, [loading, user, navigate, location.pathname, location.search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner label="Loading session" />
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <SiteProvider>
      <HelpDrawerProvider>
        <ProtectedShell />
      </HelpDrawerProvider>
    </SiteProvider>
  );
}

// RootRoute resolves `/` to the appropriate landing page:
//   - no sites exist -> /setup
//   - otherwise -> the sticky site from resolveLastSite() (the SAME resolver
//     the workspace nav uses, so `/` and the nav always agree on which site
//     is "current"): stored last-active slug if still valid, else first
//     active, else first of any status.
//   - no resolvable site (defensive) -> /sites lobby
function RootRoute() {
  const status = useSetupStatus();
  const { allSites, loading } = useCurrentSite();
  if (status.loading || loading) {
    return <LoadingSpinner label="Loading dashboard" />;
  }
  if (allSites.length === 0) {
    return <Navigate to="/setup" replace />;
  }
  const target = resolveLastSite(allSites);
  if (!target) {
    return <Navigate to="/sites" replace />;
  }
  return <Navigate to={`/sites/${target.slug}`} replace />;
}

export default function App() {
  return (
    <Suspense fallback={<div className="p-6"><RoutePendingSkeleton /></div>}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<ProtectedLayout />}>
          <Route index element={<RootRoute />} />
          <Route path="/setup" element={<SetupPage />} />

          {/* Global pages (no site context). */}
          <Route path="/sites" element={<SitesPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/queue" element={<QueuePage />} />
          <Route path="/prompts" element={<PromptsPage />} />
          <Route path="/prompts/:type" element={<PromptsPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="/help/section/:tier" element={<HelpPage />} />
          <Route path="/help/:slug" element={<HelpPage />} />

          {/* Site-scoped pages — SiteLayout resolves :slug to a site and
              gates rendering on it being found. */}
          <Route path="/sites/:slug" element={<SiteLayout />}>
            <Route index element={<SiteDetailPage />} />
            <Route path="brainstorm" element={<BrainstormPage />} />
            <Route path="pipeline" element={<PipelinePage />} />
            <Route path="drafts" element={<DraftsPage />} />
            <Route path="drafts/:id" element={<DraftDetailPage />} />
            <Route path="articles" element={<ArticlesPage />} />
            <Route path="articles/:id" element={<ArticleDetailPage />} />
            <Route path="queue" element={<QueuePage />} />
            <Route path="content-graph" element={<ContentGraphPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="alerts" element={<AlertsPage />} />
            {/* Per-site prompt editing and authority sources are tabs on
                SiteDetailPage from Phase N3; the routes below mount the page
                with the matching tab pre-selected so deep links keep
                working. */}
            <Route path="prompts" element={<SiteDetailPage initialTab="prompts" />} />
            <Route path="prompts/:type" element={<SiteDetailPage initialTab="prompts" />} />
            <Route path="authority-sources" element={<SiteDetailPage initialTab="authority" />} />
            <Route path="components" element={<SiteDetailPage initialTab="components" />} />
            {/* Site pages (CMS): tab alias + new-page form + detail editor. */}
            <Route path="pages" element={<SiteDetailPage initialTab="pages" />} />
            <Route path="pages/new" element={<NewPageForm />} />
            <Route path="pages/:pageId" element={<PageDetail />} />
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
